import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import * as templateApi from 'api/templateApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import {
  editActions,
  editSelectors,
} from 'store/reducers/apiKeyTemplateReducer/editSlice';
import { registrationActions } from 'store/reducers/apiKeyTemplateReducer/registrationSlice';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { ApiKeyTemplate } from 'types/apiKeyTemplate';
import { EditHookReturnType } from '../types';
import { useListStore } from './useListStore';

/**
 * APIキーテンプレート登録 store の実装をコンポーネントから隠すための中間層
 */
export const useEditStore = (): EditHookReturnType => {
  const dispatch = useAppDispatch();
  const { fetchApiKeyTemplates } = useListStore();
  const toast = useToast();
  const form = useForm<ApiKeyTemplate>({
    mode: 'all',
  });

  const { getValues, reset, handleSubmit } = form;

  const currentModalStep = useAppSelector(editSelectors.currentModalStep);

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    dispatch(editActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchApiKeyTemplates();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: ApiKeyTemplate) => {
      dispatch(loaderActions.setLoading(true));
      const templateId = values.id ? values.id : 0;
      const result = await templateApi.editApiKeyTemplate(templateId, values);
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: `APIキーテンプレートの登録に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: 'APIキーテンプレートの登録に失敗しました。',
      });
    },
  );

  const onOpenModalDelete = useCallback(() => {
    dispatch(editActions.updateIsOpenModalDelete(true));
  }, [dispatch]);

  const selectedApiKeyTemplate = useAppSelector(
    editSelectors.selectedApiKeyTemplate,
  );

  useEffect(() => {
    if (selectedApiKeyTemplate) {
      reset(selectedApiKeyTemplate);
    }
  }, [selectedApiKeyTemplate, reset]);

  return {
    mode: '更新',
    isOpenModal: !!selectedApiKeyTemplate,
    currentModalStep,
    onOpenModal: () => null,
    onCloseModal,
    onCloseModalWhenCompleted,
    onConfirm,
    onGoBack,
    onSubmit,
    onOpenModalDelete,
    form,
    values: getValues(),
    clawlers: selectedApiKeyTemplate?.clawlers,
  };
};
